import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Crown, X, Fire, ArrowRight, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getPublicStats } from "../lib/publicStats";
import { useAuth } from "../lib/auth";

/**
 * Floating launch-offer banner. Appears after a short delay (~2 s) on non-partner
 * business detail pages. Displays scarcity (X / 100 spots taken) + urgency (-50 %),
 * with a contextual CTA:
 *  - Logged in → direct claim via `onClaim()`
 *  - Logged out → inline email capture → magic-link with redirect back to claim
 */
const LAUNCH_LIMIT = 100;
const STORAGE_KEY = "mc68_launch_banner_dismissed";

export default function LaunchOfferBanner({ businessName, onClaim }) {
  const [visible, setVisible] = useState(false);
  const [taken, setTaken] = useState(null);
  const [mode, setMode] = useState("idle"); // idle | capture | sent
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    let alive = true;
    getPublicStats().then((r) => {
      if (!alive) return;
      const t = r.data?.premium_businesses ?? 0;
      setTaken(t);
      if (t < LAUNCH_LIMIT) {
        const timer = setTimeout(() => setVisible(true), 1800);
        return () => clearTimeout(timer);
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  const handlePrimary = () => {
    if (user) {
      if (onClaim) onClaim();
      dismiss();
      return;
    }
    // Not logged in → open capture form
    setMode("capture");
  };

  const sendMagic = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
      toast.error("Email invalide");
      return;
    }
    setSending(true);
    try {
      const redirect = `${pathname}${search}${search.includes("claim=") ? "" : (search ? "&" : "?") + "claim=1"}`;
      await api.post("/auth/magic-link", {
        email: trimmed,
        language: "fr",
        redirect,
      });
      setMode("sent");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur d'envoi";
      toast.error(msg);
    }
    setSending(false);
  };

  if (taken === null || taken >= LAUNCH_LIMIT) return null;
  const remaining = LAUNCH_LIMIT - taken;
  const progress = Math.min(100, (taken / LAUNCH_LIMIT) * 100);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 280, damping: 22 }}
          className="fixed bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6 z-40 max-w-sm"
          data-testid="launch-offer-banner"
        >
          <div className="relative overflow-hidden rounded-sm border-2 border-secondary bg-card shadow-[0_24px_60px_-18px_rgba(200,121,90,0.55)]">
            <div className="absolute inset-0 -z-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(120% 80% at 0% 0%, rgba(200,121,90,0.22), transparent 55%)" }} />
            <button
              onClick={dismiss}
              aria-label="Fermer"
              className="absolute top-2 right-2 z-10 w-7 h-7 grid place-items-center text-muted-foreground hover:text-foreground hover:bg-accent/50 rounded-sm transition-colors"
              data-testid="launch-offer-close"
            >
              <X size={14} weight="bold" />
            </button>

            <div className="relative p-5">
              {mode !== "sent" && (
                <>
                  <div className="inline-flex items-center gap-1.5 bg-secondary/15 text-secondary text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-sm mb-3">
                    <Fire size={12} weight="fill" /> Offre lancement
                  </div>
                  <div className="font-display text-xl leading-tight font-medium mb-1.5">
                    <span className="text-gradient">−50 %</span> la 1<sup>re</sup> année
                  </div>
                  <div className="text-xs text-muted-foreground leading-relaxed mb-4">
                    Réservée aux <b>100 premiers commerçants</b> partenaires du 68.
                    {businessName ? <> Et si c'était <b>{businessName}</b> ?</> : null}
                  </div>

                  <div className="mb-4" data-testid="launch-offer-scarcity">
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground mb-1.5">
                      <span>{taken} / {LAUNCH_LIMIT} places prises</span>
                      <span className="text-secondary">Plus que {remaining}</span>
                    </div>
                    <div className="h-1.5 bg-accent rounded-sm overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                        className="h-full bg-gradient-to-r from-primary to-secondary"
                      />
                    </div>
                  </div>
                </>
              )}

              {mode === "idle" && (
                <>
                  {onClaim ? (
                    <button
                      onClick={handlePrimary}
                      className="inline-flex items-center justify-center gap-1.5 w-full bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2.5 text-sm font-semibold rounded-sm transition-all hover:shadow-[0_12px_28px_-10px_rgba(200,121,90,0.55)]"
                      data-testid="launch-offer-cta"
                    >
                      <Crown size={14} weight="fill" /> J'en profite maintenant
                      <ArrowRight size={12} />
                    </button>
                  ) : (
                    <Link
                      to="/passez-partenaire"
                      onClick={dismiss}
                      className="inline-flex items-center justify-center gap-1.5 w-full bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2.5 text-sm font-semibold rounded-sm transition-all"
                      data-testid="launch-offer-cta"
                    >
                      <Crown size={14} weight="fill" /> Voir l'offre
                      <ArrowRight size={12} />
                    </Link>
                  )}
                  <div className="text-[10px] text-muted-foreground text-center mt-2 opacity-80">
                    Sans engagement · Résiliable à tout moment
                  </div>
                </>
              )}

              {mode === "capture" && (
                <form onSubmit={sendMagic} className="space-y-2" data-testid="launch-offer-capture-form">
                  <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">Votre email pro</label>
                  <div className="flex items-center gap-0 border border-secondary/60 rounded-sm overflow-hidden focus-within:border-secondary">
                    <EnvelopeSimple size={14} className="ml-3 text-secondary" />
                    <input
                      autoFocus
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@exemple.fr"
                      className="flex-1 bg-transparent px-2 py-2 text-sm outline-none"
                      data-testid="launch-offer-email"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2.5 text-sm font-semibold rounded-sm disabled:opacity-60"
                    data-testid="launch-offer-send"
                  >
                    {sending ? "Envoi…" : "Recevoir mon lien d'accès"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("idle")}
                    className="w-full text-[11px] text-muted-foreground hover:text-foreground"
                  >
                    ← Retour
                  </button>
                  <p className="text-[10px] text-muted-foreground leading-snug mt-1">
                    Lien magique sans mot de passe · valable 30 min · vous reviendrez directement sur cette fiche pour la réclamer.
                  </p>
                </form>
              )}

              {mode === "sent" && (
                <div className="text-center py-2" data-testid="launch-offer-sent">
                  <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 text-primary grid place-items-center mb-3">
                    <CheckCircle size={28} weight="fill" />
                  </div>
                  <div className="font-display text-lg font-medium mb-1">Lien envoyé !</div>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                    Ouvrez votre boîte <b>{email}</b> et cliquez sur le lien sécurisé. Vous reviendrez ici pour réclamer votre fiche en 1 clic.
                  </p>
                  <button
                    onClick={dismiss}
                    className="text-xs font-semibold text-primary hover:text-secondary"
                    data-testid="launch-offer-close-success"
                  >
                    Fermer
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
